import React from 'react';
import './Sidebar.css';

interface SidebarProps {
  activeWidget: string | null;
  onWidgetToggle: (widget: string) => void;
  isMobile: boolean;
  hasPanelOpen: boolean;
  onSettingsClick?: () => void;
  isSettingsActive?: boolean;
  onActivityClick?: () => void;
  isActivityActive?: boolean;
  onPeopleClick?: () => void;
  isPeopleActive?: boolean;
}

// Minimal geometric notepad icon
const NotepadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="3" width="14" height="18" rx="1" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="12" y2="16" />
  </svg>
);

// Minimal geometric settings/gear icon
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Minimal geometric activity/chart icon
const ActivityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

// Minimal geometric people/users icon
const PeopleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const Sidebar: React.FC<SidebarProps> = ({
  activeWidget,
  onWidgetToggle,
  isMobile,
  hasPanelOpen,
  onSettingsClick,
  isSettingsActive = false,
  onActivityClick,
  isActivityActive = false,
  onPeopleClick,
  isPeopleActive = false,
}) => {
  const handleNotepadClick = () => {
    onWidgetToggle(activeWidget === 'notepad' ? '' : 'notepad');
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const handleActivityClick = () => {
    if (onActivityClick) {
      onActivityClick();
    }
  };

  const handlePeopleClick = () => {
    if (onPeopleClick) {
      onPeopleClick();
    }
  };

  return (
    <div className={`sidebar ${hasPanelOpen ? 'has-panel' : ''}`}>
      <div className="sidebar-top">
        <button
          className={`sidebar-icon ${activeWidget === 'notepad' ? 'active' : ''}`}
          onClick={handleNotepadClick}
          aria-label="Notepad"
        >
          <NotepadIcon />
          <span className="sidebar-icon-label">Notepad</span>
        </button>
      </div>
      <div className="sidebar-bottom">
        <button
          className={`sidebar-icon ${isPeopleActive ? 'active' : ''}`}
          onClick={handlePeopleClick}
          aria-label="People"
        >
          <PeopleIcon />
          <span className="sidebar-icon-label">People</span>
        </button>
        <button
          className={`sidebar-icon ${isActivityActive ? 'active' : ''}`}
          onClick={handleActivityClick}
          aria-label="Activity"
        >
          <ActivityIcon />
          <span className="sidebar-icon-label">Activity</span>
        </button>
        <button
          className={`sidebar-icon ${isSettingsActive ? 'active' : ''}`}
          onClick={handleSettingsClick}
          aria-label="Settings"
        >
          <SettingsIcon />
          <span className="sidebar-icon-label">Settings</span>
        </button>
      </div>
    </div>
  );
};
